import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { PaymentService } from '../payment.service';
import { ReportDateBy } from '../payment.type';

/** The browser's own calendar day, as YYYY-MM-DD — not the UTC one. */
function localToday(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Downloads the accountant's Excel report for one day. The day is the one this
 * browser sees, so the browser's timezone is sent along and the workbook's
 * times are in it too.
 */
@Component({
  selector: 'app-report-export-dialog',
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatButtonToggleModule, MatCheckboxModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatProgressSpinnerModule,
  ],
  templateUrl: './report-export-dialog.html',
  styles: `
    .hint { font-size: 0.85rem; opacity: 0.75; margin: 0 0 1rem; }
    .date { width: 100%; margin-bottom: 8px; }
    .date-by { display: flex; margin: 4px 0 4px; font-size: 13px; }
    .date-by mat-button-toggle { flex: 1; }
    .date-by-hint { font-size: 12px; opacity: 0.7; margin: 0 0 12px; }
    .filters { font-size: 12px; opacity: 0.8; margin: 12px 0 0; }
    .error { color: #ef9a9a; font-size: 12px; margin: 8px 0 0; }
  `,
})
export class ReportExportDialog {
  private paymentService = inject(PaymentService);
  readonly dialogRef = inject(MatDialogRef<ReportExportDialog>);
  readonly data = inject<{ supplierId?: string; supplierLabel?: string; status?: string; dateBy?: ReportDateBy }>(MAT_DIALOG_DATA, { optional: true }) ?? {};

  readonly tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  readonly max = localToday();

  date = localToday();
  includeProofs = true;
  /** '' = recorded or paid on the day, as the report always did. */
  dateBy: ReportDateBy = this.data.dateBy ?? '';
  downloading = false;
  error = '';

  download(): void {
    if (!this.date || this.downloading) return;
    this.downloading = true;
    this.error = '';
    this.paymentService.downloadSettlementsReport({
      date: this.date,
      tz: this.tz,
      supplierId: this.data.supplierId,
      status: this.data.status,
      includeProofs: this.includeProofs,
      dateBy: this.dateBy,
    }).subscribe({
      next: ({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        this.dialogRef.close(true);
      },
      // The body of a failed blob request is itself a Blob holding the JSON error.
      error: async err => {
        this.error = 'Failed to build the report';
        try {
          const body = err?.error instanceof Blob ? JSON.parse(await err.error.text()) : err?.error;
          if (body?.error) this.error = body.error;
        } catch { /* keep the generic message */ }
        this.downloading = false;
      },
    });
  }
}
